import { Controller } from "@hotwired/stimulus"

// Typeahead search for adding card dependencies across projects.
// Fetches results from the card_dependencies#search endpoint.
export default class extends Controller {
  static targets = ["input", "results", "programFilter", "hiddenInput"]
  static values = {
    searchUrl: String,
    createUrl: String
  }

  connect() {
    this._debounceTimer = null
    this._handleClickOutside = this._handleClickOutside.bind(this)
    document.addEventListener("click", this._handleClickOutside)
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    document.removeEventListener("click", this._handleClickOutside)
  }

  onInput() {
    clearTimeout(this._debounceTimer)
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this._hideResults()
      return
    }

    this._debounceTimer = setTimeout(() => this._fetchResults(query), 300)
  }

  onProgramChange() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 2) {
      this._fetchResults(query)
    }
  }

  selectCard(event) {
    event.preventDefault()
    const cardId = event.currentTarget.dataset.cardId
    this._hideResults()
    this.inputTarget.value = ""
    this._submitDependency(cardId)
  }

  _fetchResults(query) {
    let url = `${this.searchUrlValue}?query=${encodeURIComponent(query)}`

    if (this.hasProgramFilterTarget && this.programFilterTarget.value) {
      url += `&program_id=${this.programFilterTarget.value}`
    }

    fetch(url, {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content
      }
    })
      .then(response => response.json())
      .then(cards => this._renderResults(cards))
      .catch(() => this._hideResults())
  }

  _renderResults(cards) {
    if (!cards.length) {
      this.resultsTarget.innerHTML = `<div class="px-3 py-2 text-sm text-gray-400">No matching cards found</div>`
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const html = cards.map(card => {
      const stateClass = card.state === "started"
        ? "bg-green-100 text-green-700"
        : "bg-gray-100 text-gray-600"

      return `<button type="button"
        class="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm"
        data-action="click->dependency-search#selectCard"
        data-card-id="${card.id}">
        <span class="font-medium text-gray-900 truncate">#${card.id} &ndash; ${this._escapeHtml(card.name)}</span>
        <span class="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full ${stateClass}">${card.state}</span>
        <span class="flex-shrink-0 text-xs text-gray-400 ml-auto truncate max-w-[150px]">${this._escapeHtml(card.project_name)}</span>
      </button>`
    }).join("")

    this.resultsTarget.innerHTML = html
    this.resultsTarget.classList.remove("hidden")
  }

  _hideResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.innerHTML = ""
    }
  }

  _submitDependency(cardId) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = this.createUrlValue

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) {
      const csrfInput = document.createElement("input")
      csrfInput.type = "hidden"
      csrfInput.name = "authenticity_token"
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    const cardInput = document.createElement("input")
    cardInput.type = "hidden"
    cardInput.name = "depends_on_card_id"
    cardInput.value = cardId
    form.appendChild(cardInput)

    document.body.appendChild(form)
    form.requestSubmit()
    form.remove()
  }

  _handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this._hideResults()
    }
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
